/**
 * Comment Thread Component
 *
 * Displays threaded comments with reply functionality.
 * PURELY PRESENTATIONAL - uses mocked data or passed comments prop.
 *
 * Features:
 * - Threaded comments (1 level deep)
 * - Comment input box
 * - Edit/delete on own comments
 * - Reply button for threading
 * - Character count (500 max)
 * - Optimistic UI for new comments
 */

import * as React from 'react';
import type { Comment } from '@/types/job-activity';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { formatRelativeTime } from '@/lib/timeUtils';
import { cn } from '@/lib/utils';
import {
  useJobComments,
  useAddComment,
  useEditComment,
  // useDeleteComment, // DISABLED: audit trail compliance
} from '@/application/service-provider/hooks/useJobComments';
import { mapCommentDtosToComments } from '@/application/service-provider/mappers/CommentMapper';

const MAX_COMMENT_LENGTH = 500;

export interface CommentThreadProps {
  jobId: string;
  currentUserId?: string;
}

/**
 * Get user initials from name
 */
function getUserInitials(userName: string | undefined): string {
  if (!userName) return 'U';
  const parts = userName.split(' ').filter(p => p.length > 0);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return userName.substring(0, Math.min(2, userName.length)).toUpperCase();
}

export function CommentThread({
  jobId,
  currentUserId = 'user-1',
}: CommentThreadProps) {
  const [newCommentText, setNewCommentText] = React.useState('');
  const [replyingTo, setReplyingTo] = React.useState<string | null>(null);

  // Fetch comments from API
  const { data: commentsData, isLoading, error } = useJobComments(jobId);
  const addCommentMutation = useAddComment(jobId);
  const editCommentMutation = useEditComment();
  // const deleteCommentMutation = useDeleteComment(); // DISABLED: audit trail compliance

  // Map DTOs to component types
  const comments = React.useMemo(() => {
    if (!commentsData) return [];
    return mapCommentDtosToComments(commentsData);
  }, [commentsData]);

  // Sort by timestamp (oldest first for comments)
  const sortedComments = React.useMemo(() => {
    return [...comments].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [comments]);

  const handleAddComment = () => {
    if (newCommentText.trim()) {
      addCommentMutation.mutate({
        content: newCommentText,
        parentCommentId: replyingTo || undefined,
      });
      setNewCommentText('');
      setReplyingTo(null);
    }
  };

  const handleEditComment = (commentId: string, text: string) => {
    editCommentMutation.mutate({
      commentId,
      request: { content: text },
    });
  };

  const handleDeleteComment = (_commentId: string) => {
    // DISABLED: Comment deletion not allowed for audit trail compliance
    throw new Error('Comment deletion is not allowed for audit trail compliance');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setNewCommentText('');
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  return (
    <div className="space-y-6">
      {/* Comment Input */}
      <CommentInput
        value={newCommentText}
        onChange={setNewCommentText}
        onSubmit={handleAddComment}
        onCancel={replyingTo ? handleCancelReply : undefined}
        isReply={!!replyingTo}
        maxLength={MAX_COMMENT_LENGTH}
      />

      {/* Comments List */}
      {sortedComments.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {sortedComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              currentUserId={currentUserId}
              onReply={setReplyingTo}
              onEdit={handleEditComment}
              onDelete={handleDeleteComment}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CommentInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  isReply?: boolean;
  maxLength: number;
}

function CommentInput({
  value,
  onChange,
  onSubmit,
  onCancel,
  isReply = false,
  maxLength,
}: CommentInputProps) {
  const remainingChars = maxLength - value.length;
  const isOverLimit = remainingChars < 0;

  return (
    <div className="space-y-2">
      {isReply && (
        <div className="text-sm text-gray-600 flex items-center gap-2">
          <span>Replying to comment</span>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Cancel
            </button>
          )}
        </div>
      )}
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isReply ? 'Write a reply...' : 'Add a comment...'}
        className="resize-none"
        rows={3}
      />
      <div className="flex items-center justify-between">
        <span
          className={cn(
            'text-xs',
            isOverLimit ? 'text-red-600 font-medium' : 'text-gray-500'
          )}
        >
          {remainingChars} characters remaining
        </span>
        <Button
          onClick={onSubmit}
          disabled={!value.trim() || isOverLimit}
          size="sm"
        >
          {isReply ? 'Reply' : 'Comment'}
        </Button>
      </div>
    </div>
  );
}

interface CommentItemProps {
  comment: Comment;
  currentUserId: string;
  onReply: (commentId: string) => void;
  onEdit?: (commentId: string, text: string) => void;
  onDelete?: (commentId: string) => void;
}

function CommentItem({
  comment,
  currentUserId,
  onReply,
  onEdit,
  onDelete,
}: CommentItemProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editText, setEditText] = React.useState(comment.text);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const isOwnComment = comment.userId === currentUserId;
  const initials = getUserInitials(comment.userName);
  const relativeTime = formatRelativeTime(comment.createdAt);
  const wasEdited = comment.editedAt && comment.editedAt !== comment.updatedAt;

  const handleSaveEdit = () => {
    if (onEdit && editText.trim()) {
      onEdit(comment.id, editText);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(comment.text);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(comment.id);
    }
    setShowDeleteConfirm(false);
  };

  return (
    <div className="flex gap-3">
      {/* Avatar */}
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
      </Avatar>

      {/* Content */}
      <div className="flex-1">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-start justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-gray-900">
                {comment.userName}
              </span>
              <span className="text-xs text-gray-500">{relativeTime}</span>
              {wasEdited && (
                <span className="text-xs text-gray-500 italic">(edited)</span>
              )}
            </div>
            {isOwnComment && !isEditing && (
              <div className="flex gap-1">
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                >
                  Edit
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-xs text-red-500 hover:text-red-700 px-2 py-1"
                >
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* Comment Text */}
          {isEditing ? (
            <div className="space-y-2">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="resize-none text-sm"
                rows={3}
              />
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} size="sm" variant="default">
                  Save
                </Button>
                <Button onClick={handleCancelEdit} size="sm" variant="outline">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {comment.text}
            </p>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="flex gap-3 mt-2 ml-3">
            <button
              onClick={() => onReply(comment.id)}
              className="text-xs font-medium text-gray-500 hover:text-primary-600"
            >
              Reply
            </button>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="mt-2 ml-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 mb-2">
              Are you sure you want to delete this comment?
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleDelete}
                size="sm"
                variant="destructive"
              >
                Delete
              </Button>
              <Button
                onClick={() => setShowDeleteConfirm(false)}
                size="sm"
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="text-4xl mb-4">💬</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        No Comments Yet
      </h3>
      <p className="text-sm text-gray-600">
        Start the conversation by adding the first comment.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3">
          <div className="h-8 w-8 bg-gray-200 rounded-full" />
          <div className="flex-1">
            <div className="bg-gray-100 rounded-lg p-3 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/4" />
              <div className="h-3 bg-gray-200 rounded w-full" />
              <div className="h-3 bg-gray-200 rounded w-3/4" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ error }: { error: Error }) {
  const errorMessage = error.message.includes('401')
    ? 'Session expired. Please log in again.'
    : error.message.includes('403')
    ? "You don't have permission to view this."
    : error.message.includes('404')
    ? 'Job not found.'
    : error.message.includes('5 minute')
    ? 'Comments can only be edited within 5 minutes of posting.'
    : 'Unable to load comments. Please check your connection.';

  return (
    <div className="text-center py-12">
      <div className="text-4xl mb-4">⚠️</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Error Loading Comments
      </h3>
      <p className="text-sm text-gray-600">{errorMessage}</p>
    </div>
  );
}
